import React, { useState, useEffect } from "react";
import { useParams, useHistory } from "react-router-dom";
import { FormValidate } from "../../../components/formValidate";
import TextField from "../../../components/inputs/textfield";
import Loader from "../../../components/loader/loader";
import Snackbar from "../../../components/snackbar/snackbar";
import { user_company_users_create, user_company_users_update, user_company_users_list } from "../user.api";
import "./style.css";

let required = ["name", "email", "company"];

export default function CompanyUserManage(props) {
  const { id } = useParams();
  const history = useHistory();
  const isEdit = Boolean(id);
  
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    company: "",
    designation: "",
    department: "",
    address: "",  
    city: "",
    state: "",
    pin_code: "",
    profile_image: null
  });
  
  const [errors, setErrors] = useState({});
  const [isActive, setIsActive] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [snackMessage, setSnackMessage] = useState("");
  const [isSnackError, setIsSnackError] = useState(false);
  const [snackOpen, setSnackOpen] = useState(false);
  const [imagePreview, setImagePreview] = useState(null);

  const getErrorMessage = (fieldName) => {
    if (!errors[fieldName]) return "";
    
    switch(fieldName) {
      case "name":
        return "Name is required";
      case "email":
        return "Valid email is required";
      case "company":
        return "Company is required";
      default:
        return "This field is required";
    }
  };

  useEffect(() => {
    if (isEdit) {
      fetchUserDetails();
    }
  }, [id, isEdit]);

  const fetchUserDetails = () => {
    setIsActive(true);
    user_company_users_list().then((res) => {
      const users = res.data || [];
      const user = users.find(u => u.id === parseInt(id));
      if (user) {
        setFormData({
          name: user.name || "",
          email: user.email || "",
          phone: user.phone || "",
          company: user.company || "",
          designation: user.designation || "",
          department: user.department || "",
          address: user.address || "",
          city: user.city || "",
          state: user.state || "",
          pin_code: user.pin_code || "",
          profile_image: null
        });
        setImagePreview(user.profile_image || null);
      }
      setIsActive(false);
    }).catch((error) => {
      console.error("Error fetching user details:", error);
      setIsActive(false);
    });
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    
    // Validate field on change if form has been submitted
    if (submitted) {
      const fieldValidation = FormValidate({ [name]: value }, [name]);
      setErrors(prev => ({
        ...prev,
        [name]: fieldValidation[name] || false
      }));
    } else {
      // Clear error for this field when user starts typing
      if (errors[name]) {
        setErrors(prev => ({
          ...prev,
          [name]: false
        }));
      }
    }
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setFormData(prev => ({
        ...prev,
        profile_image: file
      }));
      
      // Create preview
      const reader = new FileReader();
      reader.onloadend = () => {
        setImagePreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    setSubmitted(true);
    
    const validationErrors = FormValidate(formData, required);
    setErrors(validationErrors);
    
    if (Object.keys(validationErrors).length === 0) {
      setIsActive(true);
      
      const data = new FormData();
      Object.keys(formData).forEach(key => {
        if (key === 'profile_image' && formData[key] instanceof File) {
          data.append('profile_image', formData[key]);
        } else if (formData[key]) {
          data.append(key, formData[key]);
        }
      });

      const apiCall = isEdit 
        ? user_company_users_update(id, data)
        : user_company_users_create(data);

      apiCall
        .then((res) => {
          setSnackMessage(res.message || `Company user ${isEdit ? 'updated' : 'created'} successfully`);
          setIsSnackError(false);
          setSnackOpen(true);
          setSubmitted(false);
          setIsActive(false);
          
          setTimeout(() => {
            history.push("/directory-company-users");
          }, 1500);
        })
        .catch((error) => {
          setSnackMessage(error.message || `Failed to ${isEdit ? 'update' : 'create'} user`);
          setIsSnackError(true);
          setSnackOpen(true);
          setSubmitted(false);
          setIsActive(false);
        });
    } else {
      setSubmitted(false);
    }
  };

  const snackbarClose = () => {
    setSnackMessage("");
    setIsSnackError(false);
    setSnackOpen(false);
  };

  const handleCancel = () => {
    history.push("/directory-company-users");
  };

  return (
    <div className="tender bg-tender">
      <Loader open={isActive} />
      <Snackbar
        message={snackMessage}
        error={isSnackError}
        open={snackOpen}
        close={snackbarClose}
      />
      <div>
        <div className="dash-banner">
          <div className="tit-txt">
            {props.state.language === "ma"
              ? (isEdit ? "കമ്പനി ഉപയോക്താവിനെ എഡിറ്റ് ചെയ്യുക" : "പുതിയ കമ്പനി ഉപയോക്താവിനെ ചേർക്കുക")
              : (isEdit ? "Edit Company User" : "Add New Company User")}
          </div>
        </div>
      </div>
      <div className="container p-50">
        <div className="user-form-container">
          <form onSubmit={handleSubmit} className="user-form">
            <div className="row">
              <div className="col-md-6">
                <div className="form-section">
                  <h4>Basic Information</h4>
                  
                  <TextField
                    type="text"
                    name="name"
                    label="Full Name *"
                    value={formData.name}
                    onChange={handleChange}
                    error={errors.name}
                    required
                    helperText={errors.name ? getErrorMessage('name') : ''}
                  />
                  
                  <TextField
                    type="email"
                    name="email"
                    label="Email Address *"
                    value={formData.email}
                    onChange={handleChange}
                    error={errors.email}
                    required
                    helperText={errors.email ? getErrorMessage('email') : ''}
                  />
                  
                  <TextField
                    type="text"
                    name="phone"
                    label="Phone Number"
                    value={formData.phone}
                    onChange={handleChange}
                    helperText={errors.phone ? getErrorMessage('phone') : ''}
                  />
                  
                  <TextField
                    type="text"
                    name="company"
                    label="Company *"
                    value={formData.company}
                    onChange={handleChange}
                    error={errors.company}
                    required
                    helperText={errors.company ? getErrorMessage('company') : ''}
                  />
                  
                  <TextField
                    type="text"
                    name="designation"
                    label="Designation"
                    value={formData.designation}
                    onChange={handleChange}
                    helperText={errors.designation ? getErrorMessage('designation') : ''}
                  />
                  
                  <TextField
                    type="text"
                    name="department"
                    label="Department"
                    value={formData.department}
                    onChange={handleChange}
                    helperText={errors.department ? getErrorMessage('department') : ''}
                  />
                </div>
              </div>
              
              <div className="col-md-6">
                <div className="form-section">
                  <h4>Contact Information</h4>
                  
                  <TextField
                    type="text"
                    name="address"
                    label="Address"
                    value={formData.address}
                    onChange={handleChange}
                    helperText={errors.address ? getErrorMessage('address') : ''}
                  />
                  
                  <TextField
                    type="text"
                    name="city"
                    label="City"
                    value={formData.city}
                    onChange={handleChange}
                    helperText={errors.city ? getErrorMessage('city') : ''}
                  />
                  
                  <TextField
                    type="text"
                    name="state"
                    label="State"
                    value={formData.state}
                    onChange={handleChange}
                    helperText={errors.state ? getErrorMessage('state') : ''}
                  />
                  
                  <TextField
                    type="text"
                    name="pin_code"
                    label="Pin Code"
                    value={formData.pin_code}
                    onChange={handleChange}
                    helperText={errors.pin_code ? getErrorMessage('pin_code') : ''}
                  />
                  
                  <div className="form-group">
                    <label>Profile Image</label>
                    <input
                      type="file"
                      name="profile_image"
                      onChange={handleImageChange}
                      accept="image/*"
                      className="form-control"
                    />
                    {imagePreview && (
                      <div className="image-preview mt-2">
                        <img
                          src={imagePreview.startsWith('data:') ? imagePreview : `${process.env.REACT_APP_BASE_URL?.replace('/api', '')}/${imagePreview}`}
                          alt="Profile Preview"
                          className="preview-image"
                        />
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
            
            <div className="form-actions">
              <button
                type="button"
                className="btn btn-secondary mr-2"
                onClick={handleCancel}
              >
                Cancel
              </button>
              <button
                type="submit"
                className="btn btn-primary"
                disabled={submitted}
              >
                {submitted ? 'Saving...' : (isEdit ? 'Update' : 'Save')}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}
